import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ProjectProfile,
  ProjectProfilesResponse,
  ApiProjectProfilesResponse,
} from '../../models/project/project-profile.model';
import {
  ProfileFieldsResponse,
  AllProfileFieldsResponse,
  CreateUpdateProfilePayload,
  CreateUpdateProfileResponse,
} from '../../models/charity/charity.model';
import { RestfulApiService } from '../restful-api.service';

// Re-export types for convenience
export type { ProjectProfile, ProjectProfilesResponse };
export type { ProfileFieldsResponse, AllProfileFieldsResponse, CreateUpdateProfilePayload, CreateUpdateProfileResponse };

export interface ToggleDefaultProjectProfileResponse {
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectProfileService {
  constructor(private api: RestfulApiService) {}

  /**
   * Get all project profiles
   */
  getAllProfiles(): Observable<ProjectProfilesResponse> {
    return this.api.get('/v1/profiles/get-all/PROJECT').pipe(
      map((apiResponse: any) => this.mapApiResponseToProfiles(apiResponse))
    );
  }

  /**
   * Set a project profile as default
   */
  setDefaultProfile(uuid: string): Observable<ToggleDefaultProjectProfileResponse> {
    return this.api.put(`/v1/profiles/toggle-as-default/PROJECT/${uuid}`).pipe(
      map((apiResponse: any) => ({
        success: true,
        message: apiResponse.message || 'Default project profile updated successfully'
      }))
    );
  }

  /**
   * Delete a project profile
   */
  deleteProfile(uuid: string): Observable<ToggleDefaultProjectProfileResponse> {
    return this.api.delete(`/v1/profiles/delete/${uuid}`).pipe(
      map((apiResponse: any) => ({
        success: true,
        message: apiResponse.message || 'Project profile deleted successfully'
      }))
    );
  }

  /**
   * Get all project profile fields
   */
  getAllProjectProfileFields(): Observable<AllProfileFieldsResponse> {
    return this.api.get('/v1/profiles/fields/PROJECT').pipe(
      map((apiResponse: any) => ({
        success: true,
        data: apiResponse.response,
        message: 'All project profile fields retrieved successfully'
      }))
    );
  }

  /**
   * Get project profile fields by profile ID
   */
  getProjectProfileFieldsById(uuid: string): Observable<ProfileFieldsResponse> {
    return this.api.get(`/v1/profiles/${uuid}`).pipe(
      map((apiResponse: any) => ({
        success: true,
        data: apiResponse.response,
        message: 'Project profile fields retrieved successfully'
      }))
    );
  }

  /**
   * Check if a profile name is already taken
   */
  checkProfileName(name: string): Observable<boolean> {
    return this.api.get(`/v1/profiles/check-name/${encodeURIComponent(name)}`).pipe(
      map((apiResponse: any) => !!apiResponse.response)
    );
  }

  /**
   * Create or update a project profile
   */
  createOrUpdateProfile(payload: CreateUpdateProfilePayload): Observable<CreateUpdateProfileResponse> {
    return this.api.post('/v1/profiles/create-update', payload).pipe(
      map((apiResponse: any) => ({
        success: true,
        profileUuid: apiResponse.response,
        message: payload.uuid ? 'Profile updated successfully' : 'Profile created successfully'
      }))
    );
  }

  private mapApiResponseToProfiles(
    apiResponse: ApiProjectProfilesResponse
  ): ProjectProfilesResponse {
    const profiles: ProjectProfile[] = apiResponse.response.map((profile) => ({
      uuid: profile.uuid,
      name: profile.name,
      numberOfEntities: profile.numberOfEntities,
      default: profile.default,
    }));

    return {
      success: true,
      data: profiles,
      message: 'Project profiles retrieved successfully',
    };
  }
}
